import React           from 'react';
import useDesignTokens from '@/hooks/useDesignTokens';
import './SidebarTabs.scss';

export type SidebarTabOption<T extends string> = {
	value: T;
	label: string;
	icon: React.ReactNode;
	title?: string;
};

export default function SidebarTabs<T extends string>({ value, onChange, options, large }: {
	value: T;
	onChange: (value: T) => void;
	options: SidebarTabOption<T>[];
	large?: boolean;
}) {
	const tokens = useDesignTokens();
	const tabRefs = React.useRef<Record<string, HTMLDivElement | null>>({});
	const [indicator, setIndicator] = React.useState({ left: 0, width: 0 });

	const updateIndicator = React.useCallback(() => {
		const activeTab = tabRefs.current[value];
		if (!activeTab) return;

		const style = window.getComputedStyle(activeTab);
		const paddingLeft = parseFloat(style.paddingLeft) || 0;
		const paddingRight = parseFloat(style.paddingRight) || 0;

		setIndicator({
			left: activeTab.offsetLeft + paddingLeft - 10,
			width: activeTab.offsetWidth - paddingLeft - paddingRight,
		});
	}, [value]);

	React.useLayoutEffect(() => {
		updateIndicator();
	}, [updateIndicator, options]);

	React.useEffect(() => {
		window.addEventListener('resize', updateIndicator);
		return () => window.removeEventListener('resize', updateIndicator);
	}, [updateIndicator]);

	return (
		<div
			className={`sidebar-tabs${large ? ' sidebar-tabs--large' : ''}`}
			style={{
				'--sidebar-tabs-border-color': tokens.border.default,
				'--sidebar-tabs-muted-color': tokens.text.muted,
				'--sidebar-tabs-default-color': tokens.text.default,
				'--sidebar-tabs-hover-bg': tokens.surface.subtle,
				'--sidebar-tabs-active-color': tokens.color.primary,
			} as React.CSSProperties}
		>
			{options.map(option => {
				const active = option.value === value;

				return (
					<div
						key={option.value}
						ref={el => {
							tabRefs.current[option.value] = el;
						}}
						className={`sidebar-tabs__tab${active ? ' sidebar-tabs__tab--active' : ''}`}
						title={option.title}
						onClick={() => onChange(option.value)}
					>
						{option.icon}
						{option.label}
					</div>
				);
			})}

			<div
				className="sidebar-tabs__indicator"
				style={{ transform: `translateX(${indicator.left}px)`, width: indicator.width }}
			/>
		</div>
	);
}
